import React from "react";
import { FiHome } from "react-icons/fi";

import MarketingPageShell from "../components/layout/MarketingPageShell";

import SeoHead from "../components/seo/SeoHead";

import StructuredData, {
  buildOrganizationSchema,
  buildWebPageSchema,
  buildBreadcrumbSchema,
  buildArticleSchema
} from "../components/seo/StructuredData";

import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";

import RelatedArticles from "../components/blog/RelatedArticles";
import Breadcrumbs from "../components/blog/Breadcrumbs";
import ArticleMeta from "../components/blog/ArticleMeta";
import TableOfContents from "../components/blog/TableOfContents";

import useThemeMode from "../hooks/useThemeMode";
import { getMarketingTheme } from "../utils/marketingTheme";
import useI18n from "../i18n/useI18n";

import getRelatedPosts from "../utils/getRelatedPosts";

function WhatIsHookoraPage() {
  const { isDark } = useThemeMode();
  const colors = getMarketingTheme(isDark);
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);

    return value === key ||
      value === undefined ||
      value === null
      ? fallback
      : value;
  };

  const breadcrumbItems = [
    {
      label: (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          <FiHome size={14} />
          Home
        </span>
      ),
      plainLabel: "Home",
      to: "/"
    },
    {
      label: "Blog",
      plainLabel: "Blog",
      to: "/blog"
    },
    {
      label: "Hookora Guides",
      plainLabel: "Hookora Guides",
      to: "/blog/category/hookora-guides"
    },
    {
      label: tt(
        "blog.articles.whatIsHookora.title",
        "What Is Hookora?"
      ),
      plainLabel: tt(
        "blog.articles.whatIsHookora.title",
        "What Is Hookora?"
      ),
      to: "/blog/what-is-hookora"
    }
  ];

  const articleListItems = [
    tt(
      "blog.articles.whatIsHookora.list.0",
      "It connects multiple content tasks inside one workflow instead of treating each task like a separate random tool."
    ),
    tt(
      "blog.articles.whatIsHookora.list.1",
      "It helps creators build momentum when they feel stuck on wording."
    ),
    tt(
      "blog.articles.whatIsHookora.list.2",
      "It focuses on practical creator output, not just generic AI text generation."
    ),
    tt(
      "blog.articles.whatIsHookora.list.3",
      "It is designed to make content creation feel smoother, clearer, and more repeatable."
    )
  ];

  const relatedPosts =
    getRelatedPosts(
      "/blog/what-is-hookora"
    );

  return (
    <MarketingPageShell>
      <SeoHead
        title={tt(
          "blog.articles.whatIsHookora.seoTitle",
          "What Is Hookora? | Hookora Blog"
        )}
        description={tt(
          "blog.articles.whatIsHookora.seoDescription",
          "Learn what Hookora is, how it works, and why Hookora helps creators generate hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text faster."
        )}
        path="/blog/what-is-hookora"
        image="/og/ai-tools-for-creators.jpg"
        publishedTime="2026-07-04"
        lastModified="2026-07-04"
      />

      <StructuredData
        id="hookora-org-schema"
        data={buildOrganizationSchema()}
      />

      <StructuredData
        id="what-is-hookora-page-schema"
        data={buildWebPageSchema({
          name: tt(
            "blog.articles.whatIsHookora.title",
            "What Is Hookora?"
          ),
          description: tt(
            "blog.articles.whatIsHookora.schemaDescription",
            "Learn what Hookora is, how it works, and why it helps creators move from idea to stronger content faster."
          ),
          url:
            "https://hookora.app/blog/what-is-hookora",
          type: "Article"
        })}
      />

      <StructuredData
        id="what-is-hookora-article-schema"
        data={buildArticleSchema({
          headline: tt(
            "blog.articles.whatIsHookora.title",
            "What Is Hookora?"
          ),
          description: tt(
            "blog.articles.whatIsHookora.schemaDescription",
            "Learn what Hookora is, how it works, and why it helps creators move from idea to stronger content faster."
          ),
          url:
            "https://hookora.app/blog/what-is-hookora",
          image:
            "https://hookora.app/og/ai-tools-for-creators.jpg",
          keywords: [
            "what is Hookora",
            "Hookora AI",
            "AI tools for creators",
            "creator workflow",
            "viral hooks",
            "AI content workflow",
            "content creator tools"
          ],
          datePublished: "2026-07-04",
          dateModified: "2026-07-04"
        })}
      />

      <StructuredData
        id="what-is-hookora-breadcrumb-schema"
        data={buildBreadcrumbSchema(
          breadcrumbItems.map((item) => ({
            label: item.plainLabel,
            to: item.to
          }))
        )}
      />

      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "34px 20px 0"
        }}
      >
        <Breadcrumbs
          items={breadcrumbItems}
        />
      </div>

      <section
        style={{
          padding: "30px 20px 36px"
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              padding: "8px 14px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontWeight: "700",
              fontSize: "12px",
              marginBottom: "18px",
              border: isDark
                ? `1px solid ${colors.border}`
                : "none"
            }}
          >
            {tt("blog.title", "Hookora Blog")}
          </div>

          <h1
            style={{
              fontSize:
                "clamp(2.4rem, 5vw, 4rem)",
              lineHeight: "1.05",
              margin: "0 0 16px",
              color: colors.text,
              letterSpacing: "-0.03em",
              maxWidth: "900px"
            }}
          >
            {tt(
              "blog.articles.whatIsHookora.title",
              "What is Hookora?"
            )}
          </h1>

          <p
            style={{
              margin: 0,
              maxWidth: "780px",
              color: colors.textSoft,
              fontSize: "17px",
              lineHeight: "1.8"
            }}
          >
            {tt(
              "blog.articles.whatIsHookora.heroDescription",
              "Hookora is an AI workflow platform for creators that helps turn rough ideas into stronger hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text from one cleaner content system."
            )}
          </p>
        </div>
      </section>

      <ArticleMeta
        author="Hookora Team"
        publishDate="July 4, 2026"
        readTime="8 min read"
        category="Hookora Guides"
      />

      <TableOfContents
        items={[
          {
            label:
              "What Hookora helps creators do",
            id:
              "what-hookora-helps-creators-do"
          },
          {
            label:
              "Why Hookora exists",
            id:
              "why-hookora-exists"
          },
          {
            label:
              "Who Hookora is for",
            id:
              "who-hookora-is-for"
          },
          {
            label:
              "What makes Hookora different",
            id:
              "what-makes-hookora-different"
          },
          {
            label:
              "In simple terms",
            id:
              "in-simple-terms"
          }
        ]}
      />

      <section
        style={{
          padding: "0 20px 48px"
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "28px",
            padding: "30px 24px",
            boxShadow: colors.shadow,
            backdropFilter: isDark
              ? "blur(14px)"
              : "none"
          }}
        >
          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.paragraphs.0",
              "Hookora is built for creators who know that content quality is not only about having good ideas. It is also about packaging those ideas well. A strong hook, a clearer title, a better caption, and a sharper script can change how content feels before anyone even watches or reads the full thing."
            )}
          </ArticleParagraph>

          <ArticleHeading
            id="what-hookora-helps-creators-do"
            colors={colors}
          >
            {tt(
              "blog.articles.whatIsHookora.sections.whatHookoraHelps.heading",
              "What Hookora helps creators do"
            )}
          </ArticleHeading>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whatHookoraHelps.paragraph1",
              "Hookora helps creators move faster when the wording feels slow. Instead of getting stuck at the start of the process, creators can generate better options for the parts of content that usually cause friction. That includes opening hooks, video titles, social captions, hashtag sets, rewritten descriptions, short-form scripts, and thumbnail text direction."
            )}
          </ArticleParagraph>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whatHookoraHelps.paragraph2",
              "The goal is not to replace creativity. The goal is to support it. Hookora is designed to help creators feel more confident when they are shaping an idea into something publishable."
            )}
          </ArticleParagraph>

          <ArticleHeading
            id="why-hookora-exists"
            colors={colors}
          >
            {tt(
              "blog.articles.whatIsHookora.sections.whyHookoraExists.heading",
              "Why Hookora exists"
            )}
          </ArticleHeading>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whyHookoraExists.paragraph1",
              "A lot of creators lose time in the same places. The idea is there, but the hook feels weak. The title does not feel clickable enough. The caption feels flat. The script takes too long to structure. These small delays create a lot of creative friction over time."
            )}
          </ArticleParagraph>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whyHookoraExists.paragraph2",
              "Hookora exists to reduce that friction. Instead of forcing creators to use scattered tools and disconnected workflows, Hookora brings the content process into one place that feels clearer and easier to use."
            )}
          </ArticleParagraph>

          <ArticleHeading
            id="who-hookora-is-for"
            colors={colors}
          >
            {tt(
              "blog.articles.whatIsHookora.sections.whoHookoraIsFor.heading",
              "Who Hookora is for"
            )}
          </ArticleHeading>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whoHookoraIsFor.paragraph1",
              "Hookora is for creators, personal brands, marketers, and social media users who want stronger content packaging without wasting too much time on wording. It is useful for creators making YouTube videos, Shorts, Instagram posts, Reels, TikToks, and other forms of social content."
            )}
          </ArticleParagraph>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.whoHookoraIsFor.paragraph2",
              "It is also useful for growing creators in places like Nigeria, where more people are building online brands, audience-driven businesses, and creative income streams and need tools that help them move faster."
            )}
          </ArticleParagraph>

          <ArticleHeading
            id="what-makes-hookora-different"
            colors={colors}
          >
            {tt(
              "blog.articles.whatIsHookora.sections.whatMakesHookoraDifferent.heading",
              "What makes Hookora different"
            )}
          </ArticleHeading>

          <ArticleList
            colors={colors}
            items={articleListItems}
          />

          <ArticleHeading
            id="in-simple-terms"
            colors={colors}
          >
            {tt(
              "blog.articles.whatIsHookora.sections.inSimpleTerms.heading",
              "In simple terms"
            )}
          </ArticleHeading>

          <ArticleParagraph colors={colors}>
            {tt(
              "blog.articles.whatIsHookora.sections.inSimpleTerms.paragraph",
              "Hookora is a content workflow platform that helps creators go from idea to stronger output faster. If someone wants better hooks, titles, captions, scripts, and packaging without making the process feel heavier, Hookora is built for that job."
            )}
          </ArticleParagraph>

          <div
            style={{
              marginTop: "40px"
            }}
          >
            <RelatedArticles
              articles={relatedPosts}
            />
          </div>
        </div>
      </section>

      <ToolSeoCTA
        badge={tt(
          "pricingCta.badge",
          "Start free with Hookora"
        )}
        title={tt(
          "blog.articles.whatIsHookora.ctaTitle",
          "Try Hookora and build your content faster with a smoother workflow"
        )}
        description={tt(
          "blog.articles.whatIsHookora.ctaDescription",
          "If you want a cleaner way to create hooks, titles, captions, scripts, and more, Hookora gives you a practical place to start."
        )}
        primaryLabel={tt(
          "pricingCta.startFree",
          "Create Free Account"
        )}
        primaryTo="/register"
        secondaryLabel={tt(
          "pricingCta.exploreFeatures",
          "Explore Features"
        )}
        secondaryTo="/features"
      />
    </MarketingPageShell>
  );
}

function ArticleHeading({
  children,
  colors,
  id
}) {
  return (
    <h2
      id={id}
      style={{
        margin: "24px 0 12px",
        color: colors.text,
        fontSize: "28px",
        lineHeight: "1.2",
        scrollMarginTop: "120px"
      }}
    >
      {children}
    </h2>
  );
}

function ArticleParagraph({
  children,
  colors
}) {
  return (
    <p
      style={{
        margin: "0 0 16px",
        color: colors.textMuted,
        fontSize: "16px",
        lineHeight: "1.9"
      }}
    >
      {children}
    </p>
  );
}

function ArticleList({
  items,
  colors
}) {
  return (
    <ul
      style={{
        margin: "0 0 16px",
        paddingLeft: "22px",
        color: colors.textMuted,
        lineHeight: "1.9"
      }}
    >
      {(items || []).map(
        (item, index) => (
          <li
            key={`${index}-${item}`}
            style={{
              marginBottom: "10px"
            }}
          >
            {item}
          </li>
        )
      )}
    </ul>
  );
}

export default WhatIsHookoraPage;